/**
 * @file SpeakerSwapControl.tsx
 * @brief "Change person" for the speaker selected in the transcript strip,
 *        and the swap's audit line with Undo.
 *
 * @details
 * DES 02-10-2026: speaker tagging sometimes names the wrong person; this
 * changes the WHOLE speaker (every line, to-do and mention) in one go, with no
 * AI. Shown to the meeting's owner and company admins (the server enforces
 * it). The picker is a menu, as every web picker
 * ([[webpanel-searchable-menu-list]]): this meeting's other voices (picking
 * one MERGES the two, since tagging split one person), colleagues, or someone
 * else typed in the search. A confirmation says exactly what will change.
 *
 * Under it, the latest swap reads "Imran Khan → Dawood Sheikh · changed by X"
 * with Undo while the server still allows it (nothing changed since).
 *
 * @author DES
 * @date 02-10-2026
 */

import { useMemo, useState } from "react";
import { ArrowRight, History, LoaderCircle, UserRoundPen, UsersRound } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Avatar } from "@/components/ui/Avatar";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { toast } from "@/shared/hooks/useToast";
import { getInitials } from "@/shared/utils/formatters";
import {
  useAssignablePeople,
  useSwapMeetingSpeaker,
  useUndoSpeakerSwap,
  type SpeakerSwapTarget,
} from "../api";

export type SwapSpeaker = { speakerId: string; name: string; profileImageUrl?: string | null };
export type SwapAudit = {
  id: string;
  from?: { name?: string | null } | null;
  to?: { name?: string | null } | null;
  byName?: string | null;
  at?: string | null;
  undoneAt?: string | null;
};

type Item = PickerSheetItem & { target: SpeakerSwapTarget; avatar?: string | null; hint?: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SpeakerSwapControl({
  meetingId,
  speaker,
  otherSpeakers,
  swaps,
  undoId,
}: {
  meetingId: string;
  /** The voice selected in the strip; null = only the audit line shows. */
  speaker: SwapSpeaker | null;
  otherSpeakers: SwapSpeaker[];
  swaps: SwapAudit[];
  undoId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [confirm, setConfirm] = useState<{ target: SpeakerSwapTarget; name: string; merge: boolean } | null>(
    null,
  );
  const people = useAssignablePeople(open);
  const swap = useSwapMeetingSpeaker();
  const undo = useUndoSpeakerSwap();

  const items: Item[] = useMemo(() => {
    if (!speaker) return [];
    const q = search.trim();
    const ql = q.toLowerCase();
    const voices: Item[] = otherSpeakers
      .filter((s) => s.speakerId !== speaker.speakerId)
      .map((s) => ({
        id: `sp:${s.speakerId}`,
        label: s.name,
        subtitle: "Also speaks in this meeting · merges the two",
        target: { speakerId: s.speakerId },
        avatar: s.profileImageUrl,
      }));
    const voiceNames = new Set(voices.map((v) => v.label.toLowerCase()));
    const colleagues: Item[] = (people.data ?? [])
      .filter((p) => (p.name || p.email) && !voiceNames.has(String(p.name || "").toLowerCase()))
      .map((p) => ({
        id: `u:${p.id}`,
        label: p.name || p.email || "Colleague",
        subtitle: p.email || "Colleague",
        target: { userId: p.id },
        avatar: p.avatarUrl ?? null,
      }));
    const all = [...voices, ...colleagues].filter(
      (i) => !ql || i.label.toLowerCase().includes(ql) || (i.subtitle ?? "").toLowerCase().includes(ql),
    );
    // Someone else: the search text itself (a name, or an email).
    if (q && !all.some((i) => i.label.toLowerCase() === ql)) {
      const isEmail = EMAIL_RE.test(q);
      all.unshift({
        id: `ext:${q}`,
        label: q,
        subtitle: "Someone else (outside your company)",
        target: isEmail ? { name: q.split("@")[0], email: q, isExternal: true } : { name: q, isExternal: true },
      });
    }
    return all;
  }, [speaker, otherSpeakers, people.data, search]);

  const latest = [...swaps].reverse().find((s) => !s.undoneAt) ?? null;
  const canUndo = !!latest && latest.id === undoId;

  const runSwap = () => {
    if (!speaker || !confirm) return;
    swap.mutate(
      { meetingId, fromSpeakerId: speaker.speakerId, to: confirm.target },
      {
        onSuccess: () => {
          toast({
            variant: "success",
            title: `${speaker.name} → ${confirm.name}`,
            description: "Every line, to-do and mention in this meeting is updated.",
          });
        },
        onError: (e) => toast({ variant: "destructive", title: "Couldn't change the speaker", description: e.message }),
        onSettled: () => setConfirm(null),
      },
    );
  };

  return (
    <div className="space-y-1.5 px-4 pb-2 md:px-5">
      {speaker ? (
        <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
          <span className="truncate">
            Wrong person? <span className="font-medium text-foreground">{speaker.name}</span>
          </span>
          <PickerSheet<Item>
            anchor={
              <button
                type="button"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted"
              >
                <UserRoundPen className="h-3.5 w-3.5 text-brand" aria-hidden />
                Change person
              </button>
            }
            open={open}
            onOpenChange={(v) => {
              setOpen(v);
              if (!v) setSearch("");
            }}
            title={`Who was ${speaker.name} really?`}
            searchPlaceholder="Name or email…"
            searchValue={search}
            onSearchChange={setSearch}
            items={items}
            loading={people.isLoading}
            clearable={false}
            emptyText="Type a name or email to use someone else."
            renderLeftAccessory={(item) =>
              item.id.startsWith("ext:") ? (
                <span className="flex items-center justify-center rounded-full bg-secondary">
                  <UsersRound className="h-6 w-6 text-muted-foreground" />
                </span>
              ) : (
                <Avatar src={item.avatar ?? null} alt={item.label} initials={getInitials(item.label)} size="xl" />
              )
            }
            onSelect={(item) => {
              setOpen(false);
              setSearch("");
              setConfirm({ target: item.target, name: item.label, merge: item.id.startsWith("sp:") });
            }}
          />
        </div>
      ) : null}

      {latest ? (
        <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
          <History className="h-3 w-3 shrink-0" aria-hidden />
          <span className="truncate">
            {latest.from?.name || "Speaker"}
            <ArrowRight className="mx-1 inline h-3 w-3" aria-hidden />
            {latest.to?.name || "Speaker"}
            {latest.byName ? ` · changed by ${latest.byName}` : ""}
            {latest.at ? ` · ${new Date(latest.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}` : ""}
          </span>
          {canUndo ? (
            <button
              type="button"
              disabled={undo.isPending}
              onClick={() =>
                undo.mutate(
                  { meetingId, swapId: latest.id },
                  {
                    onSuccess: () => toast({ title: "Change undone" }),
                    onError: (e) =>
                      toast({ variant: "destructive", title: "Couldn't undo", description: e.message }),
                  },
                )
              }
              className="shrink-0 font-semibold text-brand hover:underline disabled:opacity-50"
            >
              {undo.isPending ? <LoaderCircle className="h-3 w-3 animate-spin" /> : "Undo"}
            </button>
          ) : null}
        </div>
      ) : null}

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && !swap.isPending && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Change {speaker?.name} to {confirm?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.merge
                ? `${confirm?.name} also speaks in this meeting, so the two become one speaker. `
                : ""}
              Every line, to-do and mention of {speaker?.name} in this meeting becomes {confirm?.name}.
              Nothing is re-generated, and you can undo it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={swap.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                runSwap();
              }}
              disabled={swap.isPending}
            >
              {swap.isPending ? "Changing…" : "Change"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
